import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, formatErr } from "@/lib/api";
import { formatPrice } from "@/data/products";
import { toast } from "sonner";
import { Copy, Download } from "lucide-react";

export default function Account() {
  const { user, logout, setUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [params] = useSearchParams();
  const tab = params.get("tab") || "orders";
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [addr, setAddr] = useState({ label:"Home", line1:"", line2:"", city:"", state:"", pincode:"", country:"India", phone:"" });

  useEffect(() => {
    if (!user) return;
    api.get("/orders").then(r => setOrders(r.data)).catch(()=>{});
    setForm({ name: user.name, phone: user.phone || "" });
  }, [user]);

  if (user === undefined) return <div className="pt-40 container-lux">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const saveProfile = async (e) => {
    e.preventDefault();
    try { const r = await api.patch("/account/profile", form); setUser(r.data); toast.success("Profile updated"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const saveAddress = async (e) => {
    e.preventDefault();
    try { const r = await api.post("/account/addresses", addr); setUser(r.data); toast.success("Address saved");
      setAddr({ label:"Home", line1:"", line2:"", city:"", state:"", pincode:"", country:"India", phone:"" });
    } catch (e) { toast.error(formatErr(e)); }
  };
  const copyOrderId = async (id) => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(id);
      else {
        const input = document.createElement("textarea");
        input.value = id; input.style.position = "fixed"; input.style.opacity = "0";
        document.body.appendChild(input); input.focus(); input.select(); document.execCommand("copy"); input.remove();
      }
      toast.success("Order ID copied", { description: id });
    } catch { toast.error("Could not copy the order ID. Please select it manually."); }
  };

  const saveOrderId = (id) => {
    const blob = new Blob([`BARIQUE Order ID\n\n${id}\n`], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `BARIQUE-Order-${id}.txt`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success("Order ID saved");
  };

  const delAddress = async (id) => {
    try { const r = await api.delete(`/account/addresses/${id}`); setUser(r.data); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="pt-32 pb-24 bg-ivory min-h-screen" data-testid="account-page">
      <div className="container-lux">
        <p className="overline text-muted-foreground">The Maison</p>
        <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <h1 className="font-serif tracking-tighter text-5xl md:text-6xl font-light">Bonjour, <em className="not-italic">{user.name}</em></h1>
          <div className="account-admin-actions">
            {user.role === "admin" && (
              <>
                <a href="/admin" className="lux-link" data-testid="admin-dashboard-link">Dashboard</a>
                <a href="/admin/orders" className="lux-link" data-testid="admin-orders-link">Orders &amp; Leads</a>
                <a href="/admin/site" className="lux-link" data-testid="admin-site-link">Site &amp; Logo</a>
                <a href="/admin/nav" className="lux-link" data-testid="admin-nav-link">Navigation</a>
                <a href="/admin/media" className="lux-link" data-testid="admin-media-link">Media Library</a>
                <a href="/admin/products" className="lux-link" data-testid="admin-products-link">Product CMS</a>
                <a href="/admin/categories" className="lux-link" data-testid="admin-categories-link">Categories</a>
                <a href="/admin/hero" className="lux-link" data-testid="admin-hero-link">Hero Slides</a>
              </>
            )}
            <button onClick={logout} className="lux-link" data-testid="logout-btn">Sign Out</button>
          </div>
        </div>

        <Tabs defaultValue={tab} className="mt-16">
          <TabsList className="w-full justify-start bg-transparent border-b border-border p-0 h-auto rounded-none">
            {[["orders","Orders"],["addresses","Addresses"],["profile","Profile"]].map(([v,l])=>(
              <TabsTrigger key={v} value={v} className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-ink data-[state=active]:shadow-none data-[state=active]:bg-transparent px-0 mr-10 pb-4 text-xs uppercase tracking-widest">{l}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="orders" className="pt-10">
            {user.role === "admin" && (
              <div className="border border-border p-5 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-sand/20">
                <div>
                  <p className="overline">Admin Order Management</p>
                  <p className="text-sm text-muted-foreground mt-2">Delete orders and change order status in the dedicated CMS.</p>
                </div>
                <a href="/admin/orders" className="lux-btn-dark py-3 px-5 text-[10px] whitespace-nowrap" data-testid="open-admin-order-management">Open Order Management</a>
              </div>
            )}
            {orders.length === 0 ? <p className="text-muted-foreground text-sm">No orders yet.</p> :
              orders.map(o => (
                <div key={o.id} className="border border-border p-6 mb-4" data-testid={`order-${o.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="overline">Order #{o.id}</p>
                      <p className="text-xs text-muted-foreground mt-1">{new Date(o.created_at).toLocaleDateString()}</p>
                    </div>
                    <p className="font-serif text-xl">{formatPrice(o.total)}</p>
                  </div>
                  <div className="mt-4 flex gap-4 overflow-x-auto">
                    {o.items.map((i, k) => (
                      <div key={k} className="flex-shrink-0 w-16 h-20 bg-muted overflow-hidden"><img src={i.image} alt="" className="w-full h-full object-cover" /></div>
                    ))}
                  </div>
                  <div className="mt-4 flex gap-3 flex-wrap items-center">
                    <p className="overline text-muted-foreground mr-auto">Status · {o.status} · Payment {o.payment_status}</p>
                    <button type="button" onClick={() => copyOrderId(o.id)} className="lux-btn-outline py-2 px-4 text-[10px] inline-flex items-center gap-2" data-testid={`copy-order-id-${o.id}`}>
                      <Copy size={13} strokeWidth={1.5} /> Copy Order ID
                    </button>
                    <button type="button" onClick={() => saveOrderId(o.id)} className="lux-btn-outline py-2 px-4 text-[10px] inline-flex items-center gap-2" data-testid={`save-order-id-${o.id}`}>
                      <Download size={13} strokeWidth={1.5} /> Save Order ID
                    </button>
                  </div>
                </div>
              ))
            }
          </TabsContent>

          <TabsContent value="addresses" className="pt-10">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                {(user.addresses||[]).length === 0 && <p className="text-muted-foreground text-sm">No saved addresses.</p>}
                {(user.addresses||[]).map(a => (
                  <div key={a.id} className="border border-border p-6 mb-4">
                    <div className="flex justify-between">
                      <p className="overline">{a.label}</p>
                      <button onClick={()=>delAddress(a.id)} className="text-xs text-muted-foreground hover:text-destructive">Remove</button>
                    </div>
                    <p className="text-sm mt-3">{a.line1}{a.line2 ? `, ${a.line2}`:""}</p>
                    <p className="text-sm">{a.city}, {a.state} {a.pincode}</p>
                    <p className="text-sm">{a.country} · {a.phone}</p>
                  </div>
                ))}
              </div>
              <form onSubmit={saveAddress} className="space-y-4">
                <p className="overline">Add Address</p>
                {["label","line1","line2","city","state","pincode","country","phone"].map(k=>(
                  <div key={k}>
                    <label className="overline text-muted-foreground">{k}</label>
                    <input className="lux-input" data-testid={`addr-${k}`} value={addr[k]} onChange={(e)=>setAddr({...addr,[k]:e.target.value})} required={["line1","city","state","pincode","phone"].includes(k)} />
                  </div>
                ))}
                <button className="lux-btn-dark w-full" data-testid="save-address">Save Address</button>
              </form>
            </div>
          </TabsContent>

          <TabsContent value="profile" className="pt-10">
            <form onSubmit={saveProfile} className="max-w-md space-y-6">
              <div>
                <label className="overline text-muted-foreground">Name</label>
                <input className="lux-input" data-testid="profile-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} />
              </div>
              <div>
                <label className="overline text-muted-foreground">Phone</label>
                <input className="lux-input" data-testid="profile-phone" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} />
              </div>
              <div>
                <label className="overline text-muted-foreground">Email</label>
                <input className="lux-input" value={user.email} readOnly />
              </div>
              <button className="lux-btn-dark" data-testid="save-profile">Save Changes</button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
